import { useContext } from 'react';
import { StateFrom } from 'xstate';
import { useSelector } from '@xstate/react';
import {
  Application,
  GeneratedDisclosures,
  Offer,
} from '@ads-bread/shared/bread/codecs';
import { FetchHandlerWrapper, HandlerWrapper } from '../../lib/handlers/base';
import { ApplicationContext, applicationMachine } from './applicationMachine';
import {
  ApplicationCheckoutOptions,
  ApplicationPrepareCheckoutOptions,
  ReCreateApplicationOptions,
} from './types/events';
import { ApplicationMachineContext } from './ApplicationMachineContext';

export type ApplicationMachineSnapshot = StateFrom<typeof applicationMachine>;

export interface UseApplicationMachineValues
  extends Omit<
    ApplicationContext,
    | 'childMachinesReady'
    | 'childMachinesRequired'
    | 'deferredInitializationEvents'
  > {
  state: ApplicationMachineSnapshot;
  fetchApplication: FetchHandlerWrapper<Application, string>;
  applicationCheckout: HandlerWrapper<ApplicationCheckoutOptions, Application>;
  applicationPrepareCheckout: HandlerWrapper<
    ApplicationPrepareCheckoutOptions,
    Application
  >;
  reCreateApplication: HandlerWrapper<ReCreateApplicationOptions, Application>;
  resetApplicationState: () => void;
  assignSelectedOffer: (selectedOffer: Offer) => void;
  assignGeneratedDisclosures: (
    generatedDisclosures: GeneratedDisclosures
  ) => void;
}

/**
 * A hook to allow interaction with the application machine and its state
 * @returns UseApplicationValues
 */
export const useApplicationMachine = (): UseApplicationMachineValues => {
  const applicationService = useContext(ApplicationMachineContext);

  if (!applicationService) {
    throw new Error('Application machine service is null!');
  }

  const state = useSelector(applicationService, (s) => s);

  const fetchApplication: FetchHandlerWrapper<Application, string> = async (
    applicationID
  ) => {
    return new Promise((resolve, reject) => {
      applicationService.send({
        type: 'SEND_FETCH_APPLICATION',
        params: {
          applicationID,
          resolve,
          reject,
        },
      });
    });
  };

  const applicationCheckoutWrapper: HandlerWrapper<
    ApplicationCheckoutOptions,
    Application
  > = async (options) => {
    return new Promise((resolve, reject) => {
      applicationService.send({
        type: 'SEND_APPLICATION_CHECKOUT',
        params: {
          context: state.context,
          options,
          resolve,
          reject,
        },
      });
    });
  };

  const applicationPrepareCheckoutWrapper: HandlerWrapper<
    ApplicationPrepareCheckoutOptions,
    Application
  > = async (options) => {
    return new Promise((resolve, reject) => {
      applicationService.send({
        type: 'SEND_APPLICATION_PREPARE_CHECKOUT',
        params: {
          context: state.context,
          options,
          resolve,
          reject,
        },
      });
    });
  };

  const reCreateApplication: HandlerWrapper<
    ReCreateApplicationOptions,
    Application
  > = async (options) => {
    return new Promise((resolve, reject) => {
      applicationService.send({
        type: 'SEND_RECREATE_APPLICATION',
        params: {
          options,
          context: state.context,
          resolve,
          reject,
        },
      });
    });
  };

  const resetApplicationState = (): void => {
    applicationService.send({ type: 'SEND_RESET_APPLICATION_STATE' });
  };

  const assignSelectedOffer = (offer: Offer): void => {
    applicationService.send({
      type: 'SEND_ASSIGN_SELECTED_OFFER',
      params: {
        selectedOffer: offer,
      },
    });
  };

  const assignGeneratedDisclosures = (
    generatedDisclosures: GeneratedDisclosures
  ): void => {
    applicationService.send({
      type: 'SEND_ASSIGN_GENERATED_DISCLOSURES',
      params: {
        generatedDisclosures,
      },
    });
  };

  return {
    state,
    application: state.context.application,
    selectedOffer: state.context.selectedOffer,
    generatedDisclosures: state.context.generatedDisclosures,
    stateMachineActorRefs: state.context.stateMachineActorRefs,
    resetApplicationState,
    fetchApplication,
    applicationCheckout: applicationCheckoutWrapper,
    applicationPrepareCheckout: applicationPrepareCheckoutWrapper,
    reCreateApplication,
    assignSelectedOffer,
    assignGeneratedDisclosures,
  };
};
